import React, { useState, useEffect, useCallback } from 'react';
import {
  Layout,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Spin,
  Space,
  Popconfirm,
  InputNumber,
  Select,
  Divider,
  Typography
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined } from '@ant-design/icons';
import axios from 'axios'; // Using axios directly for now, will refactor to service later

const { Content } = Layout;
const { Title } = Typography;
const { Option } = Select;

const LabManagementPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form] = Form.useForm();

  const fetchTemplates = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // TODO: Replace with service call: labTemplateService.getAllTemplates()
      // Removed /api/v1 prefix as it's likely handled by proxy/axios defaults
      const response = await axios.get('/lab-templates');
      setTemplates(response.data.data.templates);
    } catch (error) {
      console.error("Error fetching lab templates:", error); // Keep this log for debugging
      if (!silent) {
        // Try multiple possible error message paths
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.error || 
                            error.message || 
                            'Failed to load lab templates.';
        message.error(errorMessage);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const showAddModal = () => {
    setEditingTemplate(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const showEditModal = (template) => {
    setEditingTemplate(template);
    // Prepare form data, especially the dynamic list structure
    form.setFieldsValue({
      templateName: template.templateName,
      components: template.components.map(comp => ({ ...comp })) // Create copies
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingTemplate(null);
    form.resetFields();
  };

  const handleDelete = async (templateId) => {
    setLoading(true);
    try {
      // TODO: Replace with service call: labTemplateService.deleteTemplate(templateId)
      // Removed /api/v1 prefix
      await axios.delete(`/lab-templates/${templateId}`);
      message.success('Template deleted successfully.');
      fetchTemplates(); // Refresh list
    } catch (error) {
      console.error("Error deleting template:", error);
      // Try multiple possible error message paths
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to delete template.';
      message.error(errorMessage);
      setLoading(false); // Ensure loading is stopped on error
    }
    // setLoading(false) is handled in fetchTemplates' finally block
  };

  const onFinish = async (values) => {
    setLoading(true);
    const payload = {
      templateName: values.templateName,
      // Ensure components array is not empty and filter out any potentially empty objects if needed
      components: values.components?.filter(comp => comp && comp.name && comp.units) || [],
    };

    // Basic validation for components array
    if (payload.components.length === 0) {
        message.error('A template must have at least one component with a name and units.');
        setLoading(false);
        return;
    }

    try {
      if (editingTemplate) {
        // Update existing template
        // TODO: Replace with service call: labTemplateService.updateTemplate(editingTemplate._id, payload)
        // Removed /api/v1 prefix
        await axios.put(`/lab-templates/${editingTemplate._id}`, payload);
        
        // Close modal and reset form first
        setIsModalVisible(false);
        setEditingTemplate(null);
        form.resetFields();
        setLoading(false);
        
        // Show success message with longer duration
        message.success('Template updated successfully!', 4);
        
        // Refresh list silently to avoid interference with success message
        fetchTemplates(true);
      } else {
        // Create new template
        // TODO: Replace with service call: labTemplateService.createTemplate(payload)
        // Removed /api/v1 prefix
        await axios.post('/lab-templates', payload);
        
        // Close modal and reset form first
        setIsModalVisible(false);
        setEditingTemplate(null);
        form.resetFields();
        setLoading(false);
        
        // Show success message with longer duration
        message.success('Template created successfully!', 4);
        
        // Refresh list silently to avoid interference with success message
        fetchTemplates(true);
      }
    } catch (error) {
      console.error("Error saving template:", error);
      console.error("Full error response:", error.response); // More detailed logging
      
      // Try multiple possible error message paths
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to save template.';
      
      // Special handling for duplicate name errors
      if (error.response?.status === 400 && errorMessage.toLowerCase().includes('already exists')) {
        message.error(errorMessage, 5); // Show for 5 seconds for duplicate errors
      } else {
        message.error(errorMessage);
      }
      setLoading(false); // Ensure loading is stopped on error
    }
  };

  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'templateName',
      key: 'templateName',
      sorter: (a, b) => a.templateName.localeCompare(b.templateName),
    },
    {
      title: 'Number of Components',
      dataIndex: 'components',
      key: 'componentCount',
      render: (components) => components?.length || 0,
      sorter: (a, b) => (a.components?.length || 0) - (b.components?.length || 0),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button icon={<EditOutlined />} onClick={() => showEditModal(record)}>Edit</Button>
          <Popconfirm
            title="Are you sure you want to delete this template?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Content style={{ padding: '24px' }}>
      <Title level={2}>Lab Template Management</Title>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={showAddModal}
        style={{ marginBottom: 16 }}
      >
        Add New Template
      </Button>
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
        />
      </Spin>

      <Modal
        title={editingTemplate ? 'Edit Lab Template' : 'Add New Lab Template'}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null} // Footer is handled by Form buttons
        width={800} // Wider modal for component list
        destroyOnClose // Reset form state when modal is closed
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ components: [{ name: '', units: '', referenceRangeLow: null, referenceRangeHigh: null, referenceRangeText: '', category: '' }] }} // Start with one empty component row
        >
          <Form.Item
            name="templateName"
            label="Template Name"
            rules={[
              { required: true, message: 'Please enter the template name' },
              {
                validator: async (_, value) => {
                  if (!value) return Promise.resolve();
                  
                  // Skip validation if editing and the name hasn't changed
                  if (editingTemplate && editingTemplate.templateName === value) {
                    return Promise.resolve();
                  }
                  
                  // Check if template name already exists
                  const existingTemplate = templates.find(
                    template => template.templateName.toLowerCase() === value.toLowerCase() &&
                               template._id !== editingTemplate?._id
                  );
                  
                  if (existingTemplate) {
                    return Promise.reject(new Error(`A template with the name "${value}" already exists. Please choose a different name.`));
                  }
                  
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input placeholder="Enter a unique template name" />
          </Form.Item>

          <Divider>Components</Divider>

          <Form.List name="components">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8, borderBottom: '1px dashed #ccc', paddingBottom: '8px' }} align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'name']}
                      label="Name"
                      rules={[{ required: true, message: 'Missing component name' }]}
                      style={{ width: '180px' }}
                    >
                      <Input placeholder="Component Name" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'units']}
                      label="Units"
                      rules={[{ required: true, message: 'Missing units' }]}
                       style={{ width: '100px' }}
                    >
                      <Input placeholder="Units" />
                    </Form.Item>
                     <Form.Item
                      {...restField}
                      name={[name, 'category']}
                      label="Category"
                       style={{ width: '120px' }}
                    >
                      <Input placeholder="Category (Optional)" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'referenceRangeLow']}
                      label="Ref Low"
                       style={{ width: '80px' }}
                    >
                      <InputNumber placeholder="Low" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'referenceRangeHigh']}
                      label="Ref High"
                       style={{ width: '80px' }}
                    >
                      <InputNumber placeholder="High" style={{ width: '100%' }} />
                    </Form.Item>
                     <Form.Item
                      {...restField}
                      name={[name, 'referenceRangeText']}
                      label="Ref Text"
                       style={{ width: '120px' }}
                    >
                      <Input placeholder="e.g. Negative" />
                    </Form.Item>

                    <MinusCircleOutlined onClick={() => remove(name)} style={{ marginTop: '30px' }}/>
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Component
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Form.Item style={{ textAlign: 'right', marginTop: '24px' }}>
             <Space>
                 <Button onClick={handleCancel}>
                    Cancel
                 </Button>
                 <Button type="primary" htmlType="submit" loading={loading}>
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                 </Button>
             </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Content>
  );
};

export default LabManagementPage;
